import { useState } from "react";
import axios from "axios";
import { Image, Music, Mic, Volume2, ChevronDown, ChevronUp, FileAudio, Zap, RefreshCw, Check, AlertTriangle, Loader2 } from "lucide-react";
import AssetUploader from "./AssetUploader";
import { MultiLayerPlayer } from "./MultiLayerPlayer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const COVER_IMAGE = "https://customer-assets.emergentagent.com/job_script-executor-lab/artifacts/5lcbgepp_mm4ibs0i524clw.jpeg";

const STATUS_CONFIG = {
  script: { label: "Script", color: "bg-gray-600", icon: "S" },
  assets: { label: "Assets", color: "bg-yellow-600", icon: "A" },
  recording: { label: "Recording", color: "bg-blue-600", icon: "R" },
  mixing: { label: "Mixing", color: "bg-purple-600", icon: "M" },
  complete: { label: "Complete", color: "bg-green-600", icon: "C" }
};

const EpisodeAssetCard = ({ episode, onUpdate }) => {
  const [expanded, setExpanded] = useState(false);
  const [generatingThumb, setGeneratingThumb] = useState(false);

  const updateStatus = async (newStatus) => {
    try {
      await axios.put(`${API}/episodes/${episode.id}/status`, JSON.stringify(newStatus), {
        headers: { "Content-Type": "application/json" }
      });
      onUpdate?.();
    } catch (error) {
      console.error("Failed to update status:", error);
    }
  };

  const generateThumbnail = async () => {
    setGeneratingThumb(true);
    try {
      await axios.post(`${API}/episodes/${episode.id}/generate-thumbnail`);
      onUpdate?.();
    } catch (error) {
      console.error("Failed to generate thumbnail:", error);
      alert("Thumbnail generation failed. Please try again.");
    } finally {
      setGeneratingThumb(false);
    }
  };

  const status = STATUS_CONFIG[episode.production_status] || STATUS_CONFIG.script;
  const assetCount = [episode.thumbnail_url, episode.narrator_track_url, episode.music_bed_url, episode.sfx_url].filter(Boolean).length;

  const story = episode.full_story || "";
  const hasSfx = /\[(\*{0,2})sfx:/i.test(story);
  const hasDialogue = story.includes(":") && story.split("\n").some(l => l.trim().length > 5 && /^[A-Z\*].*:/.test(l.trim()));
  const wordCount = story.split(/\s+/).filter(Boolean).length;
  const scriptReady = wordCount >= 500 && hasSfx && hasDialogue;
  const readTime = wordCount > 0 ? Math.round(wordCount / 150) : 0;

  const scriptScore = [wordCount >= 500, hasSfx, hasDialogue, !!episode.thumbnail_url].filter(Boolean).length;

  return (
    <div className="bg-[#121212] border border-white/5 rounded-lg overflow-hidden" data-testid={`asset-episode-${episode.id}`}>
      <div className="p-4 cursor-pointer hover:bg-white/5 transition-colors" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-cover bg-center rounded flex-shrink-0"
            style={{ backgroundImage: `url(${episode.thumbnail_url ? (episode.thumbnail_url.startsWith('http') ? episode.thumbnail_url : `${BACKEND_URL}${episode.thumbnail_url}`) : COVER_IMAGE})` }} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-xs text-gray-500">S{episode.season}E{episode.episode_number}</span>
              <span className={`text-xs px-2 py-0.5 rounded ${status.color}`}>{status.label}</span>
              <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${scriptReady ? "bg-green-900/40 text-green-400 border border-green-500/20" : "bg-gray-800 text-gray-500 border border-gray-700"}`} data-testid={`script-status-${episode.id}`}>
                {scriptReady ? "SCRIPT READY" : wordCount > 0 ? "SCRIPT NEEDS WORK" : "NO SCRIPT"}
              </span>
            </div>
            <h3 className="font-semibold truncate">{episode.title}</h3>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span>Script: <span className={scriptScore >= 3 ? "text-green-400" : "text-amber-400"}>{scriptScore}/4</span></span>
              <span>Media: <span className={assetCount > 0 ? "text-blue-400" : "text-gray-600"}>{assetCount}/4</span></span>
              {wordCount > 0 && <span>{wordCount.toLocaleString()} words (~{readTime} min)</span>}
              {wordCount === 0 && <span className="text-red-400/60">No script yet</span>}
            </div>
          </div>
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>

      {expanded && (
        <div className="p-4 border-t border-white/5 space-y-4">
          {/* Status selector */}
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Production Status</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                <button key={key} onClick={() => updateStatus(key)}
                  className={`px-3 py-1 text-xs rounded transition-colors ${episode.production_status === key ? config.color + " text-white" : "bg-white/5 hover:bg-white/10"}`}
                  data-testid={`status-btn-${key}-${episode.id}`}>
                  {config.label}
                </button>
              ))}
            </div>
          </div>

          {/* Script readiness */}
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Script Status</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className={`p-2 rounded text-center text-xs ${wordCount >= 500 ? "bg-green-900/30 text-green-400" : "bg-red-900/20 text-red-400"}`}>
                {wordCount >= 500 ? <Check size={12} className="mx-auto mb-1" /> : <AlertTriangle size={12} className="mx-auto mb-1" />}
                {wordCount.toLocaleString()} words
              </div>
              <div className={`p-2 rounded text-center text-xs ${hasSfx ? "bg-green-900/30 text-green-400" : "bg-red-900/20 text-red-400"}`}>
                {hasSfx ? <Check size={12} className="mx-auto mb-1" /> : <AlertTriangle size={12} className="mx-auto mb-1" />}
                SFX Cues
              </div>
              <div className={`p-2 rounded text-center text-xs ${hasDialogue ? "bg-green-900/30 text-green-400" : "bg-red-900/20 text-red-400"}`}>
                {hasDialogue ? <Check size={12} className="mx-auto mb-1" /> : <AlertTriangle size={12} className="mx-auto mb-1" />}
                Dialogue
              </div>
              <div className={`p-2 rounded text-center text-xs ${episode.thumbnail_url ? "bg-green-900/30 text-green-400" : "bg-red-900/20 text-red-400"}`}>
                {episode.thumbnail_url ? <Check size={12} className="mx-auto mb-1" /> : <AlertTriangle size={12} className="mx-auto mb-1" />}
                Thumbnail
              </div>
            </div>
          </div>

          {/* Story preview */}
          {story && (
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Script Preview (~{readTime} min read)</p>
              <div className="max-h-[200px] overflow-y-auto p-3 bg-black/40 border border-white/5 rounded text-xs text-gray-300 font-mono leading-relaxed whitespace-pre-wrap">
                {story.substring(0, 800)}{story.length > 800 ? "..." : ""}
              </div>
            </div>
          )}

          {/* SFX Cue List */}
          {hasSfx && story && (() => {
            const sfxMatches = story.match(/\[(?:\*{0,2})(?:SFX|Sound|SOUND):\s*(.*?)\]/gi) || [];
            const cueMap = {};
            sfxMatches.forEach(m => {
              const desc = m.replace(/^\[[\*]*(?:SFX|Sound|SOUND):\s*/i, "").replace(/\]$/, "").replace(/\*+$/, "").trim();
              const key = desc.toLowerCase();
              cueMap[key] = cueMap[key] || { desc, count: 0 };
              cueMap[key].count++;
            });
            const cues = Object.values(cueMap).sort((a, b) => b.count - a.count);
            return cues.length > 0 ? (
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">SFX Cue Sheet ({sfxMatches.length} cues, {cues.length} unique)</p>
                <div className="max-h-[150px] overflow-y-auto grid grid-cols-1 md:grid-cols-2 gap-1">
                  {cues.map((cue, i) => (
                    <div key={i} className="flex items-center gap-2 px-2 py-1 bg-amber-900/10 border border-amber-500/10 rounded text-xs">
                      <Volume2 size={10} className="text-amber-400 flex-shrink-0" />
                      <span className="text-gray-300 truncate flex-1">{cue.desc}</span>
                      {cue.count > 1 && <span className="text-amber-500/60 text-[10px] flex-shrink-0">x{cue.count}</span>}
                    </div>
                  ))}
                </div>
              </div>
            ) : null;
          })()}

          {/* Asset uploaders */}
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Assets</p>
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-3 bg-white/5 rounded">
                <Image size={18} className={episode.thumbnail_url ? "text-green-500" : "text-gray-500"} />
                <span className="flex-1 text-sm">Episode Thumbnail</span>
                {episode.thumbnail_url ? (
                  <Check size={16} className="text-green-500" />
                ) : (
                  <button onClick={generateThumbnail} disabled={generatingThumb}
                    className="px-3 py-1 text-xs bg-purple-600 hover:bg-purple-700 rounded disabled:opacity-50 flex items-center gap-1"
                    data-testid={`gen-thumb-btn-${episode.id}`}>
                    {generatingThumb ? <RefreshCw size={12} className="animate-spin" /> : <Zap size={12} />}
                    {generatingThumb ? "..." : "AI Generate"}
                  </button>
                )}
              </div>
              <AssetUploader episodeId={episode.id} assetType="narrator" label="Narrator Track (Cartesia)" icon={Mic} currentUrl={episode.narrator_track_url} onUploadComplete={onUpdate} />
              <AssetUploader episodeId={episode.id} assetType="musicbed" label="Music Bed" icon={Music} currentUrl={episode.music_bed_url} onUploadComplete={onUpdate} />
              <AssetUploader episodeId={episode.id} assetType="sfx" label="Sound Effects" icon={Volume2} currentUrl={episode.sfx_url} onUploadComplete={onUpdate} />
            </div>
          </div>

          {/* Multi-layer player preview */}
          <div>
            <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Preview</p>
            <MultiLayerPlayer episode={episode} />
          </div>
        </div>
      )}
    </div>
  );
};

export default EpisodeAssetCard;
